/**
 * "How to plot": ship, dates, plot type, a signal, Plot, then save the chart
 * and find it in the Catalog. The save steps need the Catalog (a database);
 * without one the tour ends at the chart.
 */

import { catalogAvailable, selectedShip } from '../../../state/store.ts';
import { isPageVisible } from '../../../state/page-visibility.ts';
import { plotState, setPlotType } from '../../../state/plot-state.ts';
import { chartReady, plotFetching } from '../../plot/plot-ui-state.ts';
import { lastSavedChart } from '../../catalog/catalog-state.ts';
import { openExploreTab, registerTour } from '../tour-registry.ts';

const savedCard = (): Element | null =>
  lastSavedChart.value
    ? document.querySelector(`catalog-card[data-chart-id="${CSS.escape(lastSavedChart.value)}"]`)
    : null;

registerTour({
  id: 'first-plot',
  version: 2,
  title: 'How to plot',
  description: 'Pick a ship and a signal, draw your first chart, and save it to the Catalog.',
  minutes: 3,
  available: () => isPageVisible('plot'),
  next: 'first-dashboard',
  prepare: () => {
    lastSavedChart.set(null);
    openExploreTab('plot');
    // The steps after this (X and Y axes, signals) are Line's controls.
    if (plotState.value.plot_type !== 'line') setPlotType('line');
  },
  steps: [
    {
      target: 'ship',
      title: 'Pick a ship',
      text: 'Each ship has its own data. Open the list and choose a ship.',
      advance: { when: () => selectedShip.value !== null },
    },
    {
      target: 'dates',
      title: 'Choose the dates',
      text: 'The buttons pick a period that ends now. You can also type a start and an end.',
      advance: 'next',
    },
    {
      target: 'plot-type-line',
      title: 'How to draw it',
      text: 'Line shows signals over time. This tour uses Line. Try the other types after the tour.',
      advance: 'next',
    },
    {
      target: 'y-series',
      title: 'Add a signal',
      text: 'A signal is one sensor, for example engine load. Click the box, type a few letters, and pick one.',
      advance: { when: () => plotState.value.y_left.length + plotState.value.y_right.length > 0 },
    },
    {
      target: 'plot-button',
      title: 'Draw the chart',
      text: 'Click Plot.',
      advance: 'click',
    },
    {
      target: 'chart',
      title: 'This is your chart',
      text: 'Point at it to read the values. Drag across it to zoom in.',
      advance: 'next',
      ready: () => chartReady.value && !plotFetching.value,
      failed: () =>
        plotFetching.value
          ? null
          : 'The chart could not be drawn. Go back, choose other dates or another signal, and click Plot again.',
    },
    {
      target: 'save-chart',
      title: 'Keep it',
      text: 'Save puts this chart in the Catalog, so you can open it again later.',
      advance: 'click',
      needs: () => catalogAvailable.value,
    },
    {
      target: 'save-dialog',
      title: 'Give it a title',
      text: 'Write a title you will know again, then click Save to my catalog.',
      advance: { when: () => lastSavedChart.value !== null },
      needs: () => catalogAvailable.value,
      backIfGone: true,
    },
    {
      target: 'nav-catalog',
      title: 'Open the Catalog',
      text: 'Your saved charts are here. Click Catalog.',
      advance: 'click',
      needs: () => catalogAvailable.value,
    },
    {
      target: savedCard,
      title: 'Here is your chart',
      text: 'Click it to open it in Explore again. Right-click it to share, rename or delete it.',
      advance: 'next',
      needs: () => catalogAvailable.value,
    },
  ],
});
